interface ShipmentResult {
  orderId: number;
  ok: boolean;
  reason?: string;
  externalOrderId?: string;
  productName?: string;
}

interface Props {
  open: boolean;
  results: ShipmentResult[];
  onClose: () => void;
}

export function ShipmentResultDialog({ open, results, onClose }: Props) {
  if (!open) return null;
  const ok = results.filter((r) => r.ok);
  const fail = results.filter((r) => !r.ok);

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-ink-950/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ship-result-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-paper shadow-modal sm:max-h-[80vh] sm:rounded-2xl">
        <div className="flex shrink-0 flex-col gap-3 border-b border-ink-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 sm:py-5">
          <div>
            <p className="label-overline">Step 4 · 출고 확정</p>
            <h2 id="ship-result-title" className="mt-1.5 text-[18px] font-medium tracking-[-0.01em] text-ink-950 sm:text-[20px]">
              송장 전송 결과
            </h2>
          </div>
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <p className="text-[12px] text-ink-500 sm:text-[13px]">
              성공 <span className="tabular font-medium text-brand-700">{ok.length}</span>건
              {fail.length > 0 && (
                <>
                  <span className="mx-1.5 text-ink-300">·</span>
                  실패 <span className="tabular font-medium text-danger-ink">{fail.length}</span>건
                </>
              )}
            </p>
            <button className="btn-secondary btn-sm shrink-0" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>

        <div className="space-y-6 overflow-y-auto bg-canvas px-4 py-5 sm:px-6">
          {fail.length > 0 && (
            <section>
              <h3 className="mb-2.5 flex items-center gap-2 text-[13px] font-medium text-danger-ink">
                <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-danger" />
                실패 ({fail.length}건)
              </h3>
              <ul className="space-y-2">
                {fail.map((r) => (
                  <li
                    key={r.orderId}
                    className="rounded-xl border border-danger-soft bg-paper px-4 py-3 text-[13px]"
                  >
                    <p className="font-mono text-[11px] text-ink-500">
                      {r.externalOrderId ?? `주문 ID: ${r.orderId}`}
                      {r.productName && (
                        <span className="ml-2 font-sans text-ink-500">{r.productName}</span>
                      )}
                    </p>
                    <p className="mt-1 break-words text-ink-900">
                      {r.reason ?? '알 수 없는 오류'}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {ok.length > 0 && (
            <section>
              <h3 className="mb-2.5 flex items-center gap-2 text-[13px] font-medium text-brand-700">
                <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-brand-500" />
                성공 ({ok.length}건)
              </h3>
              <ul className="space-y-1.5">
                {ok.map((r) => (
                  <li
                    key={r.orderId}
                    className="rounded-lg border border-ink-200 bg-paper px-3 py-2 text-[13px]"
                  >
                    <p className="font-mono text-[11px] text-ink-500">
                      {r.externalOrderId ?? `주문 ID: ${r.orderId}`}
                      {r.productName && (
                        <span className="ml-2 font-sans text-ink-700">{r.productName}</span>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
